import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { getDisplayName, getDisplayNameForUI, getDisplayRole } from '../../../utils/displayName';
import { useNavigate, useLocation } from 'react-router-dom';
import { productService, analyticsService, orderService } from '../../../services/apiService';
import { toast } from 'react-toastify';
import Sidebar from './Sidebar';
import UsedSellerOnboardingBar from './UsedSellerOnboardingBar';
import Overview from './pages/Overview';
import Products from './pages/Products';
import NewPartListingWizard from './pages/NewPartListingWizard';
import VendorDisputes from './pages/VendorDisputes';
import Orders from './pages/Orders';
import AvailabilityRequestsPanel from '../shared/AvailabilityRequestsPanel';
import Analytics from './pages/Analytics';
import Inventory from './pages/Inventory';
import YardVehicles from './pages/YardVehicles';
import YardPartsPricing from './pages/YardPartsPricing';
import PartListingCameraWizard from './pages/PartListingCameraWizard';
import ShelfPartListingForm from './pages/ShelfPartListingForm';
import ShelfPartPricingPublish from './pages/ShelfPartPricingPublish';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Deals from '../admin/pages/Deals';
import {
  FaBox,
  FaShoppingCart,
  FaMoneyBillWave,
  FaChartLine,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSignOutAlt,
  FaHome,
  FaWarehouse,
  FaUsers,
  FaBell,
  FaArrowUp,
  FaArrowDown,
  FaFilter,
  FaDownload,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaChartBar,
  FaChartPie,
  FaStar,
  FaImage,
  FaTag,
  FaBarcode,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaBars,
  FaUser,
  FaChevronDown,
  FaSignInAlt
} from 'react-icons/fa';

const VendorDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dateRange, setDateRange] = useState('30d');
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [recentOrdersData, setRecentOrdersData] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Fetch vendor products
  useEffect(() => {
    fetchVendorProducts();
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  // Fetch recent orders
  useEffect(() => {
    fetchRecentOrders();
  }, []);

  // Auto-refresh dashboard data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData();
      fetchRecentOrders();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [dateRange]);

  const fetchVendorProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await productService.getVendorProducts();
      const productsData = response?.products ?? response?.data?.products ?? [];
      const list = Array.isArray(productsData) ? productsData : [];
      const transformedProducts = list.map((product) => ({
        ...product,
        id: product._id || product.id,
        _id: product._id || product.id,
        sku: product.sku || '',
        stock: product.stock ?? 0,
        price: product.price ?? 0,
        category: product.category || 'Other',
        sales: 0,
        rating: 4.5,
        brand: product.brand || '',
        partNumber: product.partNumber || '',
        approved: product.approved || false,
        images: product.images || [],
      }));
      
      setVendorProducts(transformedProducts);
    } catch (error) {
      console.error('Error fetching vendor products:', error);
      setVendorProducts([]);
      toast.error('Failed to fetch products');
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setDashboardLoading(true);
      const data = await analyticsService.getVendorDashboard({ dateRange });
      setDashboardData(data ?? null);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(null);
      toast.error('Failed to fetch dashboard data');
    } finally {
      setDashboardLoading(false);
    }
  };

  const fetchRecentOrders = async () => {
    try {
      setOrdersLoading(true);
      const response = await orderService.getVendorOrders();
      const data = response?.data ?? response;
      const orders = data?.orders ?? [];
      const transformedOrders = (Array.isArray(orders) ? orders : []).map(order => ({
        id: order._id || order.id,
        orderId: order.orderId,
        customer: order.shippingAddress?.name || 'Unknown',
        amount: order.vendorAmount ?? order.total ?? 0,
        status: order.status || 'Pending',
        date: order.createdAt,
        items: order.vendorItemsCount ?? order.totalItems ?? order.items?.length ?? 0,
        payment: order.paymentStatus || 'Pending'
      }));
      setRecentOrdersData(transformedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setRecentOrdersData([]);
      toast.error('Failed to fetch orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  // Generate time series data (simplified for now, can be enhanced with real data)
  const generateTimeSeriesData = (days = 30) => {
    const data = [];
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toISOString().split('T')[0],
        sales: 0,
        revenue: 0,
        orders: 0,
      });
    }
    return data;
  };

  const timeSeriesData = useMemo(() => generateTimeSeriesData(
    dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90
  ), [dateRange]);

  const stats = useMemo(() => {
    const data = dashboardData;
    if (!data || typeof data !== 'object') {
      return [
        { label: 'Total Products', value: '0', icon: FaBox, color: 'bg-blue-500', change: '0%', trend: 'neutral', route: '/vendor/dashboard/products' },
        { label: 'Orders', value: '0', icon: FaShoppingCart, color: 'bg-green-500', change: '0%', trend: 'neutral', route: '/vendor/dashboard/orders' },
        { label: 'Revenue', value: '₹0.00M', icon: FaMoneyBillWave, color: 'bg-yellow-500', change: '0%', trend: 'neutral', route: '/vendor/dashboard/analytics' },
        { label: 'Stock Value', value: '₹0.00M', icon: FaWarehouse, color: 'bg-purple-500', change: '0%', trend: 'neutral', route: '/vendor/dashboard/inventory' },
        { label: 'Low Stock Items', value: '0', icon: FaExclamationTriangle, color: 'bg-red-500', change: 'Needs attention', trend: 'neutral', route: '/vendor/dashboard/inventory' },
        { label: 'Pending Orders', value: '0', icon: FaClock, color: 'bg-orange-500', change: 'To process', trend: 'neutral', route: '/vendor/dashboard/orders' },
      ];
    }
    return [
      {
        label: 'Total Products',
        value: (data.totalProducts || 0).toLocaleString(),
        icon: FaBox,
        color: 'bg-blue-500',
        change: `+${data.changes?.products || '0.0'}%`,
        trend: parseFloat(data.changes?.products || 0) >= 0 ? 'up' : 'down',
        route: '/vendor/dashboard/products'
      },
      {
        label: 'Orders',
        value: (data.totalOrders || 0).toLocaleString(),
        icon: FaShoppingCart,
        color: 'bg-green-500',
        change: `+${data.changes?.orders || '0.0'}%`,
        trend: parseFloat(data.changes?.orders || 0) >= 0 ? 'up' : 'down',
        route: '/vendor/dashboard/orders'
      },
      {
        label: 'Revenue',
        value: `₹${((data.totalRevenue || 0) / 1000000).toFixed(2)}M`,
        icon: FaMoneyBillWave,
        color: 'bg-yellow-500',
        change: `+${data.changes?.revenue || '0.0'}%`,
        trend: parseFloat(data.changes?.revenue || 0) >= 0 ? 'up' : 'down',
        route: '/vendor/dashboard/analytics'
      },
      {
        label: 'Stock Value',
        value: `₹${((data.stockValue || 0) / 1000000).toFixed(2)}M`,
        icon: FaWarehouse,
        color: 'bg-purple-500',
        change: '+4.2%', // Can be calculated from previous period
        trend: 'up',
        route: '/vendor/dashboard/inventory'
      },
      {
        label: 'Low Stock Items',
        value: (data.lowStockItems || 0).toString(),
        icon: FaExclamationTriangle,
        color: 'bg-red-500',
        change: 'Needs attention',
        trend: 'neutral',
        route: '/vendor/dashboard/inventory'
      },
      {
        label: 'Pending Orders',
        value: (data.pendingOrders || 0).toString(),
        icon: FaClock,
        color: 'bg-orange-500',
        change: 'To process',
        trend: 'neutral',
        route: '/vendor/dashboard/orders'
      },
      {
        label: 'Cancelled Orders',
        value: (data.cancelledOrders ?? recentOrdersData.filter(o => (o.status || '').toLowerCase() === 'cancelled').length).toString(),
        icon: FaTimesCircle,
        color: 'bg-red-500',
        change: 'View all',
        trend: 'neutral',
        route: '/vendor/dashboard/orders?status=cancelled'
      },
    ];
  }, [dashboardData, recentOrdersData]);

  // Products data - use fetched products from API
  const products = useMemo(() => {
    if (searchTerm) {
      return vendorProducts.filter(p =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return vendorProducts;
  }, [vendorProducts, searchTerm]);

  // Recent orders - use real data from dashboard or orders API
  const recentOrders = useMemo(() => {
    if (dashboardData?.recentOrders && dashboardData.recentOrders.length > 0) {
      return dashboardData.recentOrders.map((order, idx) => ({
        id: order.id ?? order._id ?? idx,
        orderId: order.orderId ?? order.order_id ?? '—',
        customer: order.customer ?? 'Unknown',
        amount: order.amount ?? 0,
        status: order.status ?? 'Pending',
        date: order.date,
        items: order.items ?? 0,
        payment: order.payment ?? 'Pending'
      }));
    }
    // Fallback to orders from orders API
    return recentOrdersData.slice(0, 10);
  }, [dashboardData, recentOrdersData]);

  // Top selling products - use real data from dashboard
  const topSelling = useMemo(() => {
    if (dashboardData?.topSellingProducts && dashboardData.topSellingProducts.length > 0) {
      return dashboardData.topSellingProducts.map((product, idx) => ({
        id: product.id ?? product._id ?? idx,
        name: product.name ?? '—',
        sales: product.sales ?? 0,
        rating: product.rating ?? 4.5,
        revenue: product.revenue ?? 0,
        imageUrl: product.imageUrl
      }));
    }
    // Fallback: return empty array or products sorted by sales
    return [];
  }, [dashboardData]);

  // Determine which page to show based on location
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/vendor/dashboard' || path === '/vendor/dashboard/') return 'overview';
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/listings/new')) return 'listings-new';
    if (path.includes('/products')) return 'products';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/disputes')) return 'disputes';
    if (path.includes('/availability')) return 'availability';
    if (path.includes('/deals')) return 'deals';
    if (path.includes('/parts/add/publish')) return 'parts-add-publish';
    if (path.includes('/parts/add/details')) return 'parts-add-details';
    if (path.includes('/parts/add')) return 'parts-add';
    if (path.match(/\/yard\/[^/]+\/parts/)) return 'yard-parts';
    if (path.includes('/yard')) return 'yard';
    if (path.includes('/inventory')) return 'inventory';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/settings')) return 'settings';
    return 'overview';
  };

  const currentPage = getCurrentPage();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Reset avatar error when user changes
  useEffect(() => {
    setAvatarError(false);
  }, [user?.avatar]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileMenu && !event.target.closest('.profile-menu')) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  return (
    <div className="sp-dashboard-premium sp-dashboard-dark h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <UsedSellerOnboardingBar user={user} />
        {user?.usedSellerDetails?.payoutHeld && (
          <div className="mx-4 mt-2 rounded-lg border border-amber-500/40 bg-amber-950/30 px-4 py-2 text-sm text-amber-100">
            Payouts paused: bank verification pending
          </div>
        )}
        <header className="dashboard-header shrink-0">
          <div className="px-4 md:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition"
                  aria-label="Toggle menu"
                >
                  <FaBars className="text-xl" />
                </button>
                <div>
                  <h1 className="text-lg md:text-xl font-bold text-slate-900">Vendor Dashboard</h1>
                  <p className="text-sm text-slate-500">Welcome back, {getDisplayNameForUI(user) || 'Vendor'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-4">
                <div className="hidden md:flex items-center gap-2">
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="dashboard-select"
                  >
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                  </select>
                </div>
                <button type="button" className="relative p-2.5 text-slate-600 hover:text-primary-600 hover:bg-slate-100 rounded-xl transition" aria-label="Notifications">
                  <FaBell className="text-xl" />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary-500 rounded-full ring-2 ring-white" />
                </button>

                {/* Profile Dropdown */}
                <div className="relative profile-menu">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {user?.avatar && !avatarError ? (
                      <img loading="lazy"
                        src={user.avatar}
                        alt="Profile"
                        className="w-8 h-8 rounded-full border-2 border-gray-300"
                        onError={() => setAvatarError(true)}
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full border-2 border-gray-300 bg-blue-500 flex items-center justify-center text-white font-semibold text-sm">
                        {getDisplayNameForUI(user).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="hidden md:inline text-sm font-medium text-gray-700">{getDisplayNameForUI(user) || '—'}</span>
                    <FaChevronDown className="text-xs text-gray-500" />
                  </button>

                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-200 z-50 overflow-hidden">
                      <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-3">
                          {user?.avatar && !avatarError ? (
                            <img loading="lazy"
                              src={user.avatar}
                              alt="Profile"
                              className="w-10 h-10 rounded-full border-2 border-gray-300"
                              onError={() => setAvatarError(true)}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full border-2 border-gray-300 bg-blue-500 flex items-center justify-center text-white font-semibold">
                              {getDisplayNameForUI(user).charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{getDisplayNameForUI(user) || '—'}</p>
                            <p className="text-xs text-gray-600 capitalize">{getDisplayRole(user)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={() => {
                            navigate('/vendor/dashboard/profile');
                            setShowProfileMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-sm"
                        >
                          <FaUser className="text-sm" />
                          <span>My Profile</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/login');
                            setShowProfileMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors text-sm mt-1"
                        >
                          <FaSignInAlt className="text-sm" />
                          <span>Login</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-sm mt-1"
                        >
                          <FaSignOutAlt className="text-sm" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="dashboard-main">
          <div className="p-4 md:p-6 lg:p-8">
            {/* Stats Grid - Only visible on Overview page */}
            {currentPage === 'overview' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  const TrendIcon = stat.trend === 'up' ? FaArrowUp : stat.trend === 'down' ? FaArrowDown : null;
                  return (
                    <div 
                      key={index} 
                      className="card-hover bg-white rounded-xl shadow-md p-6 border border-gray-100 cursor-pointer transition-all hover:shadow-lg hover:scale-105"
                      onClick={() => stat.route && navigate(stat.route)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                          <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</p>
                          <div className="flex items-center gap-2">
                            {TrendIcon && (
                              <TrendIcon className={`text-sm ${stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                                }`} />
                            )}
                            <p className={`text-sm font-semibold ${stat.trend === 'up' ? 'text-green-600' : stat.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                              }`}>
                              {stat.change}
                            </p>
                            {stat.trend !== 'neutral' && <span className="text-xs text-gray-500">from last month</span>}
                          </div>
                        </div>
                        <div className={`${stat.color} p-4 rounded-xl text-white shadow-lg`}>
                          <Icon className="text-3xl" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className={`grid grid-cols-1 ${currentPage === 'overview' ? 'lg:grid-cols-3' : ''} gap-6`}>
              {/* Main Content */}
              <div className={currentPage === 'overview' ? 'lg:col-span-2 space-y-6' : 'space-y-6'}>
                {currentPage === 'parts-add' ? (
                  <PartListingCameraWizard />
                ) : currentPage === 'parts-add-details' ? (
                  <ShelfPartListingForm />
                ) : currentPage === 'parts-add-publish' ? (
                  <ShelfPartPricingPublish />
                ) : currentPage === 'disputes' ? (
                  <VendorDisputes />
                ) : (
                <div className="card bg-white rounded-xl shadow-md border border-gray-100 p-6">
                  {currentPage === 'overview' && (
                    <Overview
                      timeSeriesData={timeSeriesData}
                      dateRange={dateRange}
                      recentOrders={recentOrders}
                    />
                  )}
                  {currentPage === 'listings-new' && <NewPartListingWizard />}
                  {currentPage === 'products' && (
                    <Products
                      products={products}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      onProductAdded={fetchVendorProducts}
                    />
                  )}
                  {currentPage === 'orders' && (
                    <Orders />
                  )}
                  {currentPage === 'availability' && (
                    <AvailabilityRequestsPanel />
                  )}
                  {currentPage === 'deals' && (
                    <Deals />
                  )}
                  {currentPage === 'inventory' && (
                    <Inventory products={products} />
                  )}
                  {currentPage === 'yard' && <YardVehicles />}
                  {currentPage === 'yard-parts' && <YardPartsPricing />}
                  {currentPage === 'analytics' && (
                    <Analytics dateRange={dateRange} />
                  )}
                  {currentPage === 'settings' && (
                    <Settings />
                  )}
                  {currentPage === 'profile' && (
                    <Profile />
                  )}
                </div>
                )}
              </div>

              {/* Right Sidebar - Only visible on Overview page */}
              {currentPage === 'overview' && (
                <div className="space-y-6">

                  {/* Top Selling Products */}
                  <div className="card bg-white rounded-xl shadow-md border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Top Selling Products</h3>
                    <div className="space-y-3">
                      {topSelling.length > 0 ? (
                        topSelling.map((product, idx) => (
                          <div key={product.id || idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center text-white font-bold text-xs">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{product.name}</p>
                                <p className="text-xs text-gray-600">{product.sales || 0} sales</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <FaStar className="text-yellow-400 text-xs" />
                              <span className="text-xs text-gray-600">{product.rating || 4.5}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">No sales data available yet</p>
                      )}
                    </div>
                  </div>

                  {/* Account Info */}
                  <div className="card bg-white rounded-xl shadow-md border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Account Information</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Name</p>
                        <p className="text-sm font-semibold text-gray-900">{getDisplayNameForUI(user) || '—'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Account Type</p>
                        <p className="text-sm font-semibold text-gray-900 capitalize">{user?.role || 'Vendor'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Store Status</p>
                        <span className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          <FaCheckCircle /> Active
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default VendorDashboard;
